import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadForm() {
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      toast.success("Thanks — we'll be in touch within 24 hours.");
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="lead-form-section"
      className="py-20 lg:py-28 bg-[#F4F4F5] border-b border-[#E4E4E7]"
    >
      <div className="mx-auto max-w-6xl px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.25em] text-[#1F4DA0] mb-4">
            Free LinkedIn audit
          </p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.05]">
            Let's see what your<br />
            LinkedIn could be doing.
          </h2>
          <p className="mt-6 text-base text-[#52525B] max-w-md">
            Tell us a bit about your firm. We'll send back a free, no-obligation breakdown of your current LinkedIn profile and what we'd change.
          </p>

          <div className="mt-10 border-t border-[#09090B] pt-8 space-y-4">
            <div className="flex items-center gap-3 text-sm text-[#09090B]">
              <span className="w-1.5 h-1.5 bg-[#3FA742]" />
              <span className="font-mono uppercase tracking-widest text-xs">Free audit · No retainer</span>
            </div>
            <div className="flex items-center gap-3 text-sm text-[#09090B]">
              <span className="w-1.5 h-1.5 bg-[#1F4DA0]" />
              <span className="font-mono uppercase tracking-widest text-xs">Reply within 1 business day</span>
            </div>
            <a
              href="tel:+15106123656"
              data-testid="leadform-phone"
              className="inline-flex items-center gap-2 text-sm font-bold text-[#1F4DA0] hover:text-[#3FA742] transition-colors pt-2"
            >
              Or call us directly · 510-612-3656 →
            </a>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="lead-form"
          className="lg:col-span-7 bg-white border border-[#09090B] p-8 lg:p-10 shadow-[10px_10px_0_0_#09090B]"
        >
          {done ? (
            <div data-testid="lead-form-success" className="py-12 text-center">
              <CheckCircle2 size={48} strokeWidth={1.5} className="mx-auto text-[#1F4DA0]" />
              <h3 className="font-display font-extrabold text-2xl mt-6 tracking-tight">You're in.</h3>
              <p className="mt-3 text-[#52525B] max-w-sm mx-auto">
                We've received your details. A founder will reach out within one business day.
              </p>
              <button
                type="button"
                onClick={() => setDone(false)}
                data-testid="lead-form-reset"
                className="mt-8 border-2 border-[#09090B] px-5 py-2.5 text-sm font-bold hover:bg-[#09090B] hover:text-white transition-colors"
              >
                Submit another
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Full name" required>
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    data-testid="lead-form-name"
                    type="text"
                    required
                    placeholder="Jane Doe"
                    className="w-full border border-[#09090B] bg-white px-4 py-3 font-body text-sm placeholder:text-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#1F4DA0] focus:ring-offset-2"
                  />
                </Field>
                <Field label="Company">
                  <input
                    name="company"
                    value={form.company}
                    onChange={onChange}
                    data-testid="lead-form-company"
                    type="text"
                    placeholder="Whitcombe Books"
                    className="w-full border border-[#09090B] bg-white px-4 py-3 font-body text-sm placeholder:text-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#1F4DA0] focus:ring-offset-2"
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Work email" required>
                  <input
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    data-testid="lead-form-email"
                    type="email"
                    required
                    placeholder="jane@firm.com"
                    className="w-full border border-[#09090B] bg-white px-4 py-3 font-body text-sm placeholder:text-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#1F4DA0] focus:ring-offset-2"
                  />
                </Field>
                <Field label="Phone number">
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    data-testid="lead-form-phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    className="w-full border border-[#09090B] bg-white px-4 py-3 font-body text-sm placeholder:text-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#1F4DA0] focus:ring-offset-2"
                  />
                </Field>
              </div>

              <Field label="What does your LinkedIn look like today?">
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  data-testid="lead-form-message"
                  rows={4}
                  placeholder="Honestly, I haven't touched it in years. Mostly relying on referrals…"
                  className="w-full border border-[#09090B] bg-white px-4 py-3 font-body text-sm placeholder:text-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#1F4DA0] focus:ring-offset-2 resize-none"
                />
              </Field>

              <button
                type="submit"
                disabled={loading}
                data-testid="lead-form-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#09090B] text-white font-bold px-6 py-4 hover:bg-[#1F4DA0] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    Send me my free audit <ArrowRight size={18} />
                  </>
                )}
              </button>
              <p className="text-xs text-[#52525B] text-center">
                By submitting, you agree to be contacted by Keepsy regarding our services.
              </p>
            </div>
          )}
        </form>
      </div>
    </section>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-[#09090B] mb-2 inline-block">
        {label}{required && <span className="text-[#3FA742] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
